import { createContext, useContext, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userBusinesses, setUserBusinesses] = useState([])
  const [loading, setLoading] = useState(true)
  const [authInitialized, setAuthInitialized] = useState(false)
  const [authError, setAuthError] = useState(null)

  // Hardcode super admins
  const SUPER_ADMINS = [''] // Add more emails as needed

  useEffect(() => {
    console.log('🚀 AuthContext: Starting initialization')
    
    let mounted = true
    let timeoutId = null

    // Set a timeout to prevent infinite loading
    timeoutId = setTimeout(() => {
      if (mounted && loading) {
        console.log('⏰ Auth initialization timeout - setting as initialized')
        setLoading(false)
        setAuthInitialized(true)
      }
    }, 10000) // 10 second timeout

    const initializeAuth = async () => {
      try {
        console.log('📡 Getting initial session...')
        
        // Get initial session with timeout
        const sessionPromise = supabase.auth.getSession()
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Session fetch timeout')), 5000)
        )
        
        const { data: { session }, error } = await Promise.race([sessionPromise, timeoutPromise])
        
        if (!mounted) return
        
        if (error) {
          console.error('❌ Error getting initial session:', error)
          setAuthError(error.message)
          setUser(null)
          setUserBusinesses([])
        } else if (session?.user) {
          console.log('📡 Found existing session for:', session.user.email)
          setUser(session.user)
          setAuthError(null)
          
          // Store the token for API calls
          if (session.access_token) {
            localStorage.setItem('supabase_token', session.access_token)
          }
          
          // Load user businesses but don't block on it
          loadUserBusinesses(session.user, session.access_token).catch(err => {
            console.error('❌ Error loading user businesses:', err)
            setUserBusinesses([])
          })
        } else {
          console.log('📡 No existing session found')
          setUser(null)
          setUserBusinesses([])
          setAuthError(null)
          localStorage.removeItem('supabase_token')
        }
      } catch (error) {
        console.error('❌ Error in auth initialization:', error)
        if (mounted) {
          setAuthError(error.message)
          setUser(null)
          setUserBusinesses([])
        }
      } finally {
        if (mounted) {
          setLoading(false)
          setAuthInitialized(true)
          if (timeoutId) clearTimeout(timeoutId)
        }
      }
    }

    // Set up auth state change listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('🔄 Auth state change:', event, session?.user?.email || 'No user')
        
        if (!mounted) return

        try {
          // Handle different auth events
          switch (event) {
            case 'SIGNED_IN':
            case 'TOKEN_REFRESHED':
              if (session?.user) {
                console.log('✅ User signed in/refreshed:', session.user.email)
                setUser(session.user)
                setAuthError(null)
                
                // Store the token for API calls
                if (session.access_token) {
                  localStorage.setItem('supabase_token', session.access_token)
                }
                
                // Load businesses in background
                loadUserBusinesses(session.user, session.access_token).catch(err => {
                  console.error('❌ Error loading user businesses:', err)
                  setUserBusinesses([])
                })
              }
              setLoading(false)
              setAuthInitialized(true)
              break
              
            case 'SIGNED_OUT':
              console.log('👋 User signed out - clearing state')
              setUser(null)
              setUserBusinesses([])
              setAuthError(null)
              setLoading(false)
              setAuthInitialized(true)
              localStorage.removeItem('supabase_token')
              break
              
            case 'PASSWORD_RECOVERY':
              console.log('🔑 Password recovery event')
              setLoading(false)
              setAuthInitialized(true)
              break
              
            default:
              console.log('🔄 Other auth event:', event)
              if (!session?.user) {
                setUser(null)
                setUserBusinesses([])
                localStorage.removeItem('supabase_token')
              }
              setLoading(false)
              setAuthInitialized(true)
          }
        } catch (error) {
          console.error('❌ Error handling auth state change:', error)
          setAuthError(error.message)
          setLoading(false)
          setAuthInitialized(true)
        }
      }
    )

    // Initialize auth
    initializeAuth()

    // Cleanup function
    return () => {
      console.log('🧹 Cleaning up auth subscription')
      mounted = false
      if (timeoutId) clearTimeout(timeoutId)
      subscription?.unsubscribe()
    }
  }, [])

  const loadUserBusinesses = async (authUser, token) => {
    if (!authUser || !token) {
      setUserBusinesses([])
      return
    }
  
    try {
      console.log('🏢 Loading business assignments for:', authUser.email)
      
      // Skip business loading for super admins
      if (SUPER_ADMINS.includes(authUser.email)) {
        console.log('🛡️ Super admin detected - no business restrictions')
        setUserBusinesses([])
        return
      }
  
      // Get the base URL for API calls
      const BACKEND_URL = process.env.NEXT_PUBLIC_BACKEND_URL || 'https://sayhi-voice-engine-production.up.railway.app' // Replace with your actual Railway URL
      const apiUrl = `${BACKEND_URL}/api/dashboard/businesses`
        
      console.log('🌐 Making API call to:', apiUrl)
  
      // Call backend API - NO FALLBACK to Supabase
      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
  
      console.log('📡 API Response status:', response.status, response.statusText)
  
      if (!response.ok) {
        const errorText = await response.text()
        console.error('❌ Backend API Error:', errorText)
        throw new Error(`Backend API error! status: ${response.status} - ${errorText}`)
      }
  
      const contentType = response.headers.get('content-type')
      console.log('📄 Response content type:', contentType)
  
      if (!contentType || !contentType.includes('application/json')) {
        const htmlResponse = await response.text()
        console.error('❌ Expected JSON but got HTML - backend may not be running')
        throw new Error('Backend returned HTML instead of JSON - check if backend is running')
      }
  
      const data = await response.json()
      console.log('📊 Backend API Response data:', data)
  
      if (data.success) {
        console.log('🏢 Business assignments loaded:', data.businesses?.length || 0, 'businesses')
        
        // Transform backend data to match the expected format
        const transformedBusinesses = data.businesses?.map(business => ({
          business_phone: business.phone_number,
          role: business.role || 'admin', // Use role from backend or default to admin
          business_info: {
            business_name: business.business_name,
            business_type: business.business_type,
            active: business.active
          }
        })) || []
        
        console.log('🔄 Transformed businesses:', transformedBusinesses)
        setUserBusinesses(transformedBusinesses)
      } else {
        console.error('❌ Backend returned error:', data.error)
        // Set empty array but don't throw - this allows the user to continue
        setUserBusinesses([])
      }
    } catch (error) {
      console.error('❌ Error loading user businesses from backend:', error)
      
      // No fallback - just set empty array and let the error bubble up if needed
      setUserBusinesses([])
      
      // Optionally set an auth error to inform the user
      setAuthError(`Failed to load business data: ${error.message}`)
    }
  }
    const signIn = async (email, password) => {
    console.log('🔐 Attempting sign in for:', email)
    
    try {
      setLoading(true)
      setAuthError(null)
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password
      })
      
      if (error) {
        console.error('❌ Sign in error:', error.message)
        setAuthError(error.message)
        setLoading(false)
        return { data: null, error }
      }
      
      console.log('✅ Sign in successful for:', email)
      
      // Store the token for API calls
      if (data.session?.access_token) {
        localStorage.setItem('supabase_token', data.session.access_token)
      }
      
      return { data, error: null }
      
    } catch (error) {
      console.error('❌ Sign in exception:', error)
      setAuthError(error.message)
      setLoading(false)
      return { data: null, error }
    }
  }

  const signOut = async () => {
    console.log('🚪 Starting sign out process...')
    
    try {
      setLoading(true)
      setAuthError(null)
      
      // Clear local state immediately
      setUser(null)
      setUserBusinesses([])
      
      // Sign out from Supabase with proper scope
      const { error } = await supabase.auth.signOut({ scope: 'global' })
      
      if (error) {
        console.error('❌ Sign out error:', error)
        setAuthError(error.message)
      } else {
        console.log('✅ Sign out successful')
      }
      
      // Clear any potential cached data
      localStorage.clear()
      sessionStorage.clear()
      
      // Force reload after a short delay
      setTimeout(() => {
        window.location.replace('/login')
      }, 500)
      
      return { error }
      
    } catch (error) {
      console.error('❌ Sign out exception:', error)
      setAuthError(error.message)
      
      // Even if there's an error, clear local state and redirect
      setUser(null)
      setUserBusinesses([])
      localStorage.clear()
      sessionStorage.clear()
      
      setTimeout(() => {
        window.location.replace('/login')
      }, 500)
      
      return { error }
    } finally {
      setLoading(false)
    }
  }

  // Role checking functions
  const isSuperAdmin = () => {
    const result = user && SUPER_ADMINS.includes(user.email)
    return !!result
  }

  const isBusinessAdmin = (businessPhone = null) => {
    if (isSuperAdmin()) return true
    
    if (!businessPhone) {
      return userBusinesses.some(ub => ub.role === 'admin')
    }
    
    const userBusiness = userBusinesses.find(ub => ub.business_phone === businessPhone)
    return userBusiness?.role === 'admin'
  }

  const canAccessBusiness = (businessPhone) => {
    if (isSuperAdmin()) return true
    return userBusinesses.some(ub => ub.business_phone === businessPhone)
  }

  const getUserBusinessPhones = () => {
    if (isSuperAdmin()) {
      return [] // Empty array indicates "all businesses"
    }
    return userBusinesses.map(ub => ub.business_phone).filter(phone => phone)
  }

  const getUserRole = () => {
    if (isSuperAdmin()) return 'super_admin'
    return userBusinesses.length > 0 ? 'business_user' : 'no_access'
  }

  const getBusinessRole = (businessPhone) => {
    if (isSuperAdmin()) return 'super_admin'
    const userBusiness = userBusinesses.find(ub => ub.business_phone === businessPhone)
    return userBusiness?.role || null
  }

  // Helper function to check if auth is ready
  const isAuthReady = () => {
    return authInitialized && !loading
  }

  // Debug logging
  console.log('🎯 AuthContext state:', {
    loading,
    authInitialized,
    userEmail: user?.email,
    isSuperAdmin: isSuperAdmin(),
    businessCount: userBusinesses.length,
    authError,
    isReady: isAuthReady()
  })

  const value = {
    user,
    userBusinesses,
    loading,
    authInitialized,
    authError,
    signIn,
    signOut,
    isSuperAdmin,
    isBusinessAdmin,
    canAccessBusiness,
    getUserBusinessPhones,
    getUserRole,
    getBusinessRole,
    isAuthReady
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}